import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Redirect, useHistory, useParams } from "react-router-dom";

import Button from "../../../../components/Button";
import AdminTransparencyService, { TransparencyPeriodSummary } from "../../../../services/AdminTransparencyService";
import NewPeriodModal from "./NewPeriodModal";
import PeriodDetail from "./PeriodDetail";
import StatusTag from "./StatusTag";
import { TEMPLATE_PATH } from "./spreadsheet";
import { usePeriodTitle } from "./usePeriodTitle";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.transparency";
const BASE_PATH = "/admin/transparencia";
const SINGLE_PANE_QUERY = "(max-width: 1199.98px)";

type Status = "loading" | "ready" | "error";

export default function AdminTransparency() {
  const { t } = useTranslation();
  const periodTitle = usePeriodTitle();
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const singlePane = useMediaQuery(SINGLE_PANE_QUERY);

  const [periods, setPeriods] = useState<TransparencyPeriodSummary[]>([]);
  const [status, setStatus] = useState<Status>("loading");
  const [creating, setCreating] = useState(false);

  const loadPeriods = useCallback(() => {
    AdminTransparencyService.periods()
      .then((data) => {
        setPeriods(Array.isArray(data) ? data : []);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const handleCreated = (period: TransparencyPeriodSummary) => {
    setCreating(false);
    loadPeriods();
    history.push(`${BASE_PATH}/${period.id}`);
  };

  if (id !== undefined && !/^\d+$/.test(id)) {
    return <Redirect to="/404" />;
  }

  const selectedId = id ? Number(id) : null;
  const showList = !singlePane || selectedId === null;
  const showDetail = !singlePane || selectedId !== null;
  const selectedPeriod = periods.find((period) => period.id === selectedId);
  const publishedSibling = selectedPeriod
    ? periods.find((period) => period.id !== selectedPeriod.id && period.status === "published"
      && period.month === selectedPeriod.month && period.year === selectedPeriod.year) || null
    : null;

  return (
    <div className={`${style.layout} ${singlePane ? style.layoutSingle : ""}`}>
      {showList && (
        <section className={style.listPane} aria-labelledby="transparency-periods-title">
          <h1 className={style.visuallyHidden} id="transparency-periods-title">
            {t(`${keyI18n}.listLabel`)}
          </h1>
          <div className={style.listActions}>
            <Button
              variant="filled"
              shape="round"
              size="small"
              onClick={() => setCreating(true)}
              icon={<span className="material-symbols-sharp">add</span>}
            >
              {t(`${keyI18n}.actions.create`)}
            </Button>
          </div>

          {status === "loading" && (
            <p className={`pgm-body-large ${style.message}`} role="status">
              {t("translations.admin.home.loading")}
            </p>
          )}
          {status === "error" && (
            <p className={`pgm-body-large ${style.message}`} role="alert">
              {t(`${keyI18n}.loadError`)}
            </p>
          )}
          {status === "ready" && periods.length === 0 && (
            <p className={`pgm-body-large ${style.message}`}>{t(`${keyI18n}.empty`)}</p>
          )}
          {status === "ready" && periods.length > 0 && (
            <ul className={style.list}>
              {periods.map((period) => {
                const selected = period.id === selectedId;

                return (
                  <li key={period.id}>
                    <Link
                      to={`${BASE_PATH}/${period.id}`}
                      aria-current={selected ? "page" : undefined}
                      className={`${style.periodCard} ${selected ? style.periodCardSelected : ""}`}
                    >
                      <span className={style.periodCardHeader}>
                        <span className={`pgm-title-medium ${style.periodCardTitle}`}>
                          {periodTitle(period.month, period.year)}
                        </span>
                        <StatusTag status={period.status} />
                      </span>
                      <span className={`pgm-body-medium ${style.periodCardMeta}`}>
                        {t(`${keyI18n}.attorneysCount`, { count: period.attorneysCount })}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {showDetail && (
        <section className={style.detailPane}>
          {selectedId === null ? (
            <div className={style.emptyDetail}>
              <span aria-hidden="true" className={`material-symbols-sharp ${style.emptyIcon}`}>
                table_eye
              </span>
              <p className={`pgm-title-large ${style.emptyText}`}>{t(`${keyI18n}.selectPrompt`)}</p>
              <div className={style.emptyActions}>
                <Button
                  variant="filled"
                  shape="round"
                  size="medium"
                  onClick={() => setCreating(true)}
                  icon={<span className="material-symbols-sharp">add</span>}
                >
                  {t(`${keyI18n}.actions.create`)}
                </Button>
                <Button
                  variant="outlined"
                  shape="round"
                  size="medium"
                  href={TEMPLATE_PATH}
                  download=""
                  icon={<span className="material-symbols-sharp">download</span>}
                >
                  {t(`${keyI18n}.actions.downloadTemplate`)}
                </Button>
              </div>
            </div>
          ) : (
            <PeriodDetail
              key={selectedId}
              id={selectedId}
              backLink={singlePane ? BASE_PATH : undefined}
              publishedSibling={publishedSibling}
              onChanged={loadPeriods}
            />
          )}
        </section>
      )}

      <NewPeriodModal open={creating} onClose={() => setCreating(false)} onCreated={handleCreated} />
    </div>
  );
}

function useMediaQuery(query: string): boolean {
  const getMatches = () => typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const media = window.matchMedia(query);
    const handleChange = () => setMatches(media.matches);

    handleChange();
    media.addListener(handleChange);

    return () => media.removeListener(handleChange);
  }, [query]);

  return matches;
}
